import React from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Box,
  VStack,
  Text,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { MdArrowBack } from 'react-icons/md';
import { useRouter } from 'next/router';
import { FeatureId, UPSELL_FEATURES } from 'src/constants/tier-features';

interface UpsellAccessBlockedModalProps {
  isOpen: boolean;
  featureId: FeatureId;
}

const UpsellAccessBlockedModal = ({ isOpen, featureId }: UpsellAccessBlockedModalProps) => {
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const router = useRouter();

  const handleGoBack = () => {
    router.back();
  };

  const feature = UPSELL_FEATURES[featureId];

  if (!feature) {
    console.error(`Feature ${featureId} not found in UPSELL_FEATURES`);
    return null;
  }

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={() => {}} // eslint-disable-line @typescript-eslint/no-empty-function
      size="md"
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <AlertDialogOverlay bg="blackAlpha.600" backdropFilter="blur(10px)">
        <AlertDialogContent
          borderRadius="2xl"
          bg="white"
          boxShadow="2xl"
          border="1px solid"
          borderColor="gray.100"
          overflow="hidden"
        >
          {/* Header with gradient background */}
          <Box bgGradient="linear(135deg, orange.500, red.600)" px={6} py={8} textAlign="center">
            <VStack spacing={3}>
              <Box
                bg="whiteAlpha.200"
                borderRadius="full"
                p={4}
                backdropFilter="blur(10px)"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={feature.icon || MdArrowBack} w={8} h={8} color="white" />
              </Box>
              <AlertDialogHeader
                fontSize="2xl"
                fontWeight="bold"
                color="white"
                p={0}
                textAlign="center"
              >
                Access Blocked
              </AlertDialogHeader>
            </VStack>
          </Box>

          <AlertDialogBody px={8} py={6}>
            <VStack spacing={4} textAlign="center">
              <Text fontSize="md" color="gray.600" lineHeight="1.6">
                This feature is not available at your current tier. You need to upgrade your to
                access{' '}
                <Text as="span" fontWeight="semibold" color="orange.600">
                  {feature.displayName}
                </Text>
                .
              </Text>

              <Box
                bg="orange.50"
                borderRadius="lg"
                p={4}
                border="1px solid"
                borderColor="orange.100"
                w="full"
              >
                <Flex align="center" justify="center" gap={2}>
                  <Text fontSize="sm" color="orange.700" fontWeight="medium">
                    🔒 This feature is locked until you upgrade to a higher tier
                  </Text>
                </Flex>
              </Box>
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter px={8} pb={6} pt={2}>
            <Flex gap={3} w="full">
              <Button
                ref={cancelRef}
                onClick={handleGoBack}
                colorScheme="orange"
                flex={1}
                bgGradient="linear(135deg, orange.500, red.600)"
                _hover={{
                  bgGradient: 'linear(135deg, orange.600, red.700)',
                  transform: 'translateY(-1px)',
                  boxShadow: 'lg',
                }}
                _active={{
                  transform: 'translateY(0)',
                }}
                borderRadius="lg"
                h={12}
                fontWeight="semibold"
                leftIcon={<MdArrowBack />}
              >
                Go Back
              </Button>
              <Button
                ref={cancelRef}
                colorScheme="blue"
                onClick={() => router.push('/admin/tier-options')}
                flex={1}
                bgGradient="linear(135deg, lomaGreen.400, lomaGreen.800)"
                _hover={{
                  bgGradient: 'linear(135deg, lomaGreen.500, lomaGreen.900)',
                  transform: 'translateY(-1px)',
                  boxShadow: 'lg',
                  textDecoration: 'none',
                }}
                _active={{
                  transform: 'translateY(0)',
                }}
                borderRadius="lg"
                h={12}
                fontWeight="semibold"
              >
                Upgrade
              </Button>
            </Flex>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default UpsellAccessBlockedModal;
